import { SaveOutlined } from '@ant-design/icons'
import { DriveFile, Organization, OrganizationMember } from '@prisma/client'
import { Alert, Button, Flex, Input } from 'antd'
import { useEffect, useState } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { Api } from '~/core/trpc'
import { Icon } from '~/features/shared/icons'
import { MarkdownEditor } from '~/plugins/markdown/client/components/Editor'
import { SocketClient } from '~/plugins/socket/client'
import { useDriveFile } from '../../hooks/useDriveFile'
import { ModalPublish } from '../ModalPublish'
import { PlaceholderEmpty } from '../PlaceholderEmpty'

type Props = {
  driveFileId: string
  organization: Organization
  organizationMember: OrganizationMember
  agent: OrganizationMemberFromContext
  onSave?: (driveFileUpdated: Partial<DriveFile>) => Promise<void>
}

export const BlogPostEditor: React.FC<Props> = ({
  driveFileId,
  organization,
  organizationMember,
  agent,
  onSave,
}) => {
  const designSystem = DesignSystem.use()

  const { driveFile, updateDriveFile, isLoading, refetch } = useDriveFile({
    driveFileId,
    organizationMember,
  })

  const triggerEventMutation = Api.dataConnector.triggerEvent.useMutation()

  const [content, setContent] = useState<string>(driveFile?.content ?? '')
  const [name, setName] = useState<string>(driveFile?.name)
  const [isPublish, setIsPublish] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isNameUpdate, setIsNameUpdate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)

  const isDirty = content !== driveFile?.content || name !== driveFile?.name

  SocketClient.useEvent(
    `organization-${organization.id}-blog-post-${driveFileId}-canvas`,
    async (payload: any) => {
      if (payload?.type === 'updating') {
        setIsUpdating(true)
        return
      }
      if (payload?.type === 'updating-stop') {
        setIsUpdating(false)

        await refetch()
      }
    },
  )

  const handleChange = (contentUpdated: string) => {
    setContent(contentUpdated)
  }

  const handleSave = async () => {
    const driveFileUpdated = { driveFileId: driveFile.id, name, content }
    await updateDriveFile(driveFileUpdated)

    await onSave?.(driveFileUpdated)
  }

  useEffect(() => {
    if (driveFile) {
      setName(driveFile.name)
      setContent(driveFile.content ?? '')
    }
  }, [driveFile])

  const handlePublish = () => {
    setIsPublish(true)
  }

  const handleAskAI = async (textSelected: string, guidelines: string) => {
    try {
      setIsUpdating(true)
      await triggerEventMutation.mutateAsync({
        appId: agent.appSession.appId,
        providerKey: 'http',
        providerTriggerKey: 'NewRequest',
        appSessionId: agent.appSession.id,
        payload: {
          eventKey: 'content.toUpdate',
          _version: 2,
          blogPostId: driveFileId,
          textSelected: textSelected,
          guidelines,
        },
      })
    } catch (error) {
      setIsUpdating(false)
    }
  }

  const handleEditImage = async (imageSrc: string, prompt: string) => {
    try {
      setIsUpdating(true)

      await triggerEventMutation.mutateAsync({
        appId: agent.appSession.appId,
        providerKey: 'http',
        providerTriggerKey: 'NewRequest',
        appSessionId: agent.appSession.id,
        payload: {
          eventKey: 'content.imageToUpdate',
          _version: 2,
          blogPostId: driveFileId,
          imageSrc,
          prompt,
        },
      })
    } catch (error) {
      setIsUpdating(false)
    }
  }

  const handlePublished = async (platform: string) => {
    const driveFileUpdated = {
      driveFileId: driveFile.id,
      values: { ...(driveFile.values as any), status: 'PUBLISHED', platform },
    }

    await updateDriveFile(driveFileUpdated)
  }

  useEffect(() => {
    const closedCount = parseInt(
      localStorage.getItem('ai-alert-closed') || '0',
      10,
    )
    if (closedCount < 3) {
      setShowAlert(true)
    }
  }, [])

  const handleCloseAlert = () => {
    const closedCount = parseInt(
      localStorage.getItem('ai-alert-closed') || '0',
      10,
    )
    localStorage.setItem('ai-alert-closed', String(closedCount + 1))
    setShowAlert(false)
  }

  if (!driveFile) {
    return (
      <>
        <Flex vertical className="w-full h-full">
          <PlaceholderEmpty />
        </Flex>
      </>
    )
  }

  return (
    <Flex
      vertical
      align="center"
      className="w-full h-full mx-auto"
      style={{ overflowY: 'auto' }}
    >
      <Flex
        justify="space-between"
        align="center"
        className="w-full px-8 py-4 gap-4"
        style={{ maxWidth: 1200 }}
      >
        <div
          style={{
            flexGrow: 1,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {isNameUpdate ? (
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              onBlur={() => setIsNameUpdate(false)}
              variant="borderless"
              style={{
                fontSize: 14,
                fontWeight: 600,
                padding: 0,
                lineHeight: 1.2,
                width: '100%',
              }}
            />
          ) : (
            <div
              onClick={() => setIsNameUpdate(true)}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'text',
                fontSize: 14,
                fontWeight: 600,
              }}
              title={name}
            >
              {name || 'Untitled'}
            </div>
          )}
        </div>

        <Flex gap={8}>
          <Button
            disabled={!isDirty}
            size="small"
            icon={<SaveOutlined />}
            onClick={handleSave}
            iconPosition="end"
            type="primary"
          >
            {!designSystem.isMobile ? 'Save' : ''}
          </Button>
          <Button
            size="small"
            icon={<Icon name="paper-airplane" />}
            onClick={handlePublish}
            iconPosition="end"
            type="primary"
          >
            {!designSystem.isMobile ? 'Publish' : ''}
          </Button>
        </Flex>
      </Flex>

      {agent?.appSession?.status !== 'DISABLED' &&
        !designSystem.isMobile &&
        showAlert && (
          <Alert
            className="mb-2"
            message="Select text or click on image to edit with AI"
            type="warning"
            closable
            onClose={handleCloseAlert}
            style={{
              backgroundColor: '#fffbe6',
              borderColor: '#ffe58f',
              color: 'rgba(0,0,0,0.88)',
            }}
          />
        )}

      <Flex
        vertical
        align="center"
        className="w-full"
        flex={1}
        style={{ overflowY: 'auto' }}
      >
        <MarkdownEditor
          value={driveFile.content ?? ''}
          agent={agent}
          isUpdating={isUpdating}
          onAskAI={handleAskAI}
          onEditImage={handleEditImage}
          onChange={handleChange}
        />
      </Flex>

      {isPublish && (
        <ModalPublish
          blogPostId={driveFile.id}
          content={content}
          onClose={() => setIsPublish(false)}
          onPublish={handlePublished}
        />
      )}
    </Flex>
  )
}
